/**
 * Helper: Product images
 * -----------------------------------------------------------------------------
 * Loads additional product images which aren't available in the product data.
 * - Algolia hits only contain a single image (`image`/`product_image`) so the
 *   rollover (secondary) image and the mobile image carousel images have to
 *   be requested separately.
 * - Requests are cached in memory and session storage so each product is only
 *   requested once per session.
 * - Requests are queued and made when the browser is idle so they don't compete
 *   with the primary product images.
 *
 */
const SESSION_STORAGE_KEY = 'cnvs:product-images'
const MAX_CONCURRENT_REQUESTS = 4
const MAX_IMAGES = 5

/**
 * Cache of in-flight and resolved requests, keyed by product handle.
 */
const requestCache = new Map()

/**
 * Requests waiting for a free slot.
 */
const requestQueue = []

let activeRequests = 0
let sessionCache = null

/**
 * Get product's images.
 * - Limited to the first `MAX_IMAGES` images.
 * @param {String} handle - Product handle.
 * @returns {Promise<Array>}
 */
export function getProductImages(handle) {
  if (!handle) {
    return Promise.resolve([])
  }

  if (requestCache.has(handle)) {
    return requestCache.get(handle)
  }

  const cachedImages = getSessionCache()[handle]

  if (Array.isArray(cachedImages)) {
    const cachedRequest = Promise.resolve(cachedImages)
    requestCache.set(handle, cachedRequest)
    return cachedRequest
  }

  const request = queueRequest(handle)
  requestCache.set(handle, request)

  return request
}

/**
 * Get product's secondary (rollover) image.
 * @param {String} handle - Product handle.
 * @returns {Promise<Object|Null>}
 */
export async function getSecondaryProductImage(handle) {
  const images = await getProductImages(handle)
  return images[1] || null
}

/**
 * Queue product request.
 * - Limits concurrent requests so they don't delay other assets.
 * @param {String} handle - Product handle.
 * @returns {Promise<Array>}
 */
function queueRequest(handle) {
  return new Promise((resolve) => {
    requestQueue.push({ handle, resolve })
    processQueue()
  })
}

/**
 * Process queued requests up to the concurrency limit.
 */
function processQueue() {
  if (activeRequests >= MAX_CONCURRENT_REQUESTS || !requestQueue.length) {
    return
  }

  const { handle, resolve } = requestQueue.shift()
  activeRequests += 1

  whenIdle(async() => {
    const images = await requestProductImages(handle)

    setSessionCache(handle, images)
    resolve(images)

    activeRequests -= 1
    processQueue()
  })
}

/**
 * Run callback when the browser is idle.
 * @param {Function} callback - Callback to run.
 */
function whenIdle(callback) {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(callback, { timeout: 1000 })
    return
  }

  setTimeout(callback, 0)
}

/**
 * Request product JSON and format its images.
 * @param {String} handle - Product handle.
 * @returns {Promise<Array>}
 */
async function requestProductImages(handle) {
  try {
    const url = `${cnvs.routes.normalisedRoot}products/${encodeURIComponent(handle)}.js`
    const response = await fetch(url, { priority: 'low' })

    if (!response.ok) {
      return []
    }

    const product = await response.json()

    return formatImages(product)

  } catch {
    return []
  }
}

/**
 * Format images of product JSON response.
 * - Matches the object shape expected by `responsive-image`.
 * @param {Object} product - Product JSON (`/products/{handle}.js`) response.
 * @returns {Array}
 */
function formatImages(product) {
  const media = Array.isArray(product?.media)
    ? product.media.filter((item) => item.media_type === 'image')
    : []

  if (media.length) {
    return media
      .slice(0, MAX_IMAGES)
      .map((image, index) => ({
        alt: image.alt || product.title || '',
        aspectRatio: image.width && image.height
          ? image.width / image.height
          : null,
        height: image.height || null,
        id: image.id,
        index,
        src: image.src || image.preview_image?.src || null,
        width: image.width || null,
      }))
      .filter((image) => image.src)
  }

  const images = Array.isArray(product?.images) ? product.images : []

  return images
    .slice(0, MAX_IMAGES)
    .filter(Boolean)
    .map((src, index) => ({
      alt: product.title || '',
      aspectRatio: null,
      height: null,
      id: null,
      index,
      src,
      width: null,
    }))
}

/**
 * Get session storage cache.
 * @returns {Object}
 */
function getSessionCache() {
  if (sessionCache) {
    return sessionCache
  }

  try {
    sessionCache = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEY)) || {}
  } catch {
    sessionCache = {}
  }

  return sessionCache
}

/**
 * Save image data to the session storage cache.
 * @param {String} handle - Product handle.
 * @param {Array} images - Formatted image data.
 */
function setSessionCache(handle, images) {
  const cache = getSessionCache()
  cache[handle] = images

  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(cache))
  } catch {
    // Silently fail if session storage is full or unavailable
  }
}
